// components/CategorySelectorMultiple.js
import React, { useState, useEffect } from 'react';
import axiosInstance from '../services/axiosInstance';

const CategorySelectorMultiple = ({ 
  establishmentId, 
  selectedCategoryIds = [], 
  onCategoriesChange, 
  label = "Catégories" 
}) => {
  const [categories, setCategories] = useState([]);
  const [hierarchicalCategories, setHierarchicalCategories] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const styles = {
    container: {
      position: 'relative'
    },
    inputGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      marginBottom: '16px'
    },
    label: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#374151'
    },
    selectedContainer: {
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      padding: '12px',
      backgroundColor: '#ffffff',
      cursor: 'pointer',
      minHeight: '48px',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      alignItems: 'flex-start'
    },
    hierarchyGroup: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 8px',
      backgroundColor: '#f8fafc',
      borderRadius: '8px',
      border: '1px solid #e2e8f0'
    },
    selectedTag: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      padding: '3px 8px',
      borderRadius: '12px',
      fontSize: '11px',
      fontWeight: '500'
    },
    hierarchySeparator: {
      color: '#9ca3af',
      fontSize: '12px',
      fontWeight: '500',
      margin: '0 2px'
    },
    removeButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '0 4px',
      fontSize: '12px',
      fontWeight: 'bold',
      color: '#dc2626'
    },
    placeholder: {
      color: '#9ca3af',
      fontSize: '14px'
    },
    dropdown: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      backgroundColor: '#ffffff',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      zIndex: 1000,
      maxHeight: '300px',
      overflowY: 'auto'
    },
    categoryOption: {
      padding: '12px',
      cursor: 'pointer',
      borderBottom: '1px solid #f3f4f6',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    categoryOptionDisabled: {
      cursor: 'not-allowed',
      opacity: 0.6,
      backgroundColor: '#f9fafb'
    },
    categoryText: {
      flex: 1
    },
    categoryName: {
      fontWeight: '500',
      fontSize: '14px'
    },
    categoryPath: {
      fontSize: '12px',
      color: '#6b7280'
    },
    hierarchyInfo: {
      fontSize: '11px',
      color: '#9ca3af',
      fontStyle: 'italic',
      marginTop: '4px'
    },
    warningText: {
      color: '#dc2626',
      fontSize: '10px',
      fontStyle: 'italic'
    },
    leafIndicator: {
      fontSize: '10px',
      color: '#059669',
      fontWeight: 'bold'
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [establishmentId]);

  const fetchCategories = async () => {
    try {
      console.log(`Fetching categories for establishment ${establishmentId}`);
      const response = await axiosInstance.get(
        `create_drinks_establishment/establishment/${establishmentId}/custom-categories`
      );
      console.log('Categories fetched:', response.data);
      setCategories(response.data);
      setHierarchicalCategories(buildHierarchy(response.data));
    } catch (error) {
      console.error('Erreur lors de la récupération des catégories', error);
      console.error('Error details:', error.response?.data);
    }
  };

  const buildHierarchy = (cats) => {
    const hierarchy = [];
    
    const addCategoryWithChildren = (category, level = 0) => {
      const hasChildren = cats.some(cat => cat.parent_id === category.id);
      hierarchy.push({ ...category, level, hasChildren });
      
      // Ajouter les enfants
      const children = cats.filter(cat => cat.parent_id === category.id);
      children.forEach(child => addCategoryWithChildren(child, level + 1));
    };
    
    // Commencer par les catégories racines
    const rootCategories = cats.filter(cat => !cat.parent_id);
    rootCategories.forEach(cat => addCategoryWithChildren(cat));
    
    return hierarchy;
  };

  // Fonction pour vérifier si une catégorie a des enfants
  const hasChildren = (categoryId) => {
    return categories.some(cat => cat.parent_id === categoryId);
  };

  // Fonction pour obtenir le chemin complet d'une catégorie
  const getCategoryPath = (categoryId) => {
    const path = [];
    const category = categories.find(cat => cat.id === categoryId);
    
    if (!category) return path;
    
    let currentCategory = category;
    path.unshift(currentCategory);
    
    while (currentCategory && currentCategory.parent_id) {
      const parent = categories.find(cat => cat.id === currentCategory.parent_id);
      if (parent) {
        path.unshift(parent);
        currentCategory = parent;
      } else {
        break;
      }
    }
    
    return path;
  };

  // Fonction pour obtenir tous les ancêtres d'une catégorie
  const getCategoryAncestors = (categoryId) => {
    const ancestors = [];
    const category = categories.find(cat => cat.id === categoryId);
    
    if (!category) return ancestors;
    
    let currentCategory = category;
    while (currentCategory && currentCategory.parent_id) {
      const parent = categories.find(cat => cat.id === currentCategory.parent_id);
      if (parent) {
        ancestors.unshift(parent.id);
        currentCategory = parent;
      } else {
        break;
      }
    }
    
    return ancestors;
  };

  // Fonction pour obtenir tous les descendants d'une catégorie
  const getCategoryDescendants = (categoryId) => {
    const descendants = [];
    
    const findDescendants = (parentId) => {
      const children = categories.filter(cat => cat.parent_id === parentId);
      children.forEach(child => {
        descendants.push(child.id);
        findDescendants(child.id);
      });
    };
    
    findDescendants(categoryId);
    return descendants;
  };

  // Fonction pour obtenir toutes les catégories feuilles sélectionnées (sans enfants)
  const getLeafSelections = () => {
    return selectedCategoryIds.filter(categoryId => !hasChildren(categoryId));
  };

  // Fonction pour grouper les catégories par hiérarchie basée sur les feuilles
  const getHierarchyGroups = () => {
    const leafSelections = getLeafSelections();
    const groups = [];
    
    leafSelections.forEach(categoryId => {
      const path = getCategoryPath(categoryId);
      groups.push(path);
    });
    
    return groups;
  };

  const handleCategoryToggle = (categoryId) => {
    // Vérifier si la catégorie a des enfants
    if (hasChildren(categoryId)) {
      // Ne pas permettre la sélection directe d'une catégorie parent
      return;
    }

    const isCurrentlySelected = selectedCategoryIds.includes(categoryId);
    let newSelected = [...selectedCategoryIds];

    if (isCurrentlySelected) {
      // Déselection : retirer la catégorie et tous ses descendants
      const descendants = getCategoryDescendants(categoryId);
      newSelected = newSelected.filter(id => 
        id !== categoryId && !descendants.includes(id)
      );
      
      // Retirer aussi les ancêtres qui ne sont plus nécessaires
      const ancestors = getCategoryAncestors(categoryId);
      ancestors.forEach(ancestorId => {
        // Vérifier si cet ancêtre a encore des descendants sélectionnés
        const ancestorDescendants = getCategoryDescendants(ancestorId);
        const hasOtherSelectedDescendants = ancestorDescendants.some(descId => 
          newSelected.includes(descId) && descId !== categoryId
        );
        
        if (!hasOtherSelectedDescendants) {
          newSelected = newSelected.filter(id => id !== ancestorId);
        }
      });
    } else {
      // Sélection : ajouter la catégorie et tous ses ancêtres
      const ancestors = getCategoryAncestors(categoryId);
      const toAdd = [...ancestors, categoryId];
      
      toAdd.forEach(id => {
        if (!newSelected.includes(id)) {
          newSelected.push(id);
        }
      });
    }
    
    onCategoriesChange(newSelected);
  };

  const removeCategory = (categoryId, e) => {
    e.stopPropagation();
    
    // Supprimer cette catégorie feuille et nettoyer les ancêtres si nécessaire
    let newSelected = selectedCategoryIds.filter(id => id !== categoryId);
    
    // Nettoyer les ancêtres qui ne sont plus nécessaires
    const ancestors = getCategoryAncestors(categoryId);
    ancestors.forEach(ancestorId => {
      const ancestorDescendants = getCategoryDescendants(ancestorId);
      const hasOtherSelectedDescendants = ancestorDescendants.some(descId => 
        newSelected.includes(descId)
      );
      
      if (!hasOtherSelectedDescendants) {
        newSelected = newSelected.filter(id => id !== ancestorId);
      }
    });
    
    onCategoriesChange(newSelected);
  };

  // Fonction pour déterminer si une catégorie est sélectionnée ou partiellement sélectionnée
  const getCategorySelectionState = (categoryId) => {
    const isSelected = selectedCategoryIds.includes(categoryId);
    const descendants = getCategoryDescendants(categoryId);
    const selectedDescendants = descendants.filter(id => selectedCategoryIds.includes(id));
    
    if (isSelected) {
      return 'selected';
    } else if (selectedDescendants.length > 0) {
      return 'partial';
    }
    return 'none';
  };

  const renderHierarchyTag = (category, isLast, isLeafSelection) => {
    const tagStyle = {
      ...styles.selectedTag,
      backgroundColor: isLeafSelection ? '#e0f2fe' : '#f8fafc',
      color: '#0f172a',
      border: `1px ${isLeafSelection ? 'solid' : 'dashed'} #cbd5e1`,
      fontWeight: isLeafSelection ? '600' : '500'
    };

    return (
      <span key={category.id} style={tagStyle}>
        {category.name}
        {isLast && isLeafSelection && (
          <button
            style={styles.removeButton}
            onClick={(e) => removeCategory(category.id, e)}
            title="Supprimer cette sélection"
          >
            ×
          </button>
        )}
      </span>
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.inputGroup}>
        <label style={styles.label}>{label}</label>
        
        <div 
          style={styles.selectedContainer}
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        >
          {selectedCategoryIds.length === 0 ? (
            <span style={styles.placeholder}>Sélectionner des catégories...</span>
          ) : (
            <div style={{ width: '100%' }}>
              {getHierarchyGroups().map((hierarchyPath, groupIndex) => (
                <div key={groupIndex} style={styles.hierarchyGroup}>
                  {hierarchyPath.map((category, pathIndex) => {
                    const isLast = pathIndex === hierarchyPath.length - 1;
                    const isLeafSelection = pathIndex === hierarchyPath.length - 1;
                    
                    return (
                      <React.Fragment key={category.id}>
                        {renderHierarchyTag(category, isLast, isLeafSelection)}
                        {!isLast && (
                          <span style={styles.hierarchySeparator}>→</span>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={styles.hierarchyInfo}>
          💡 Vous ne pouvez sélectionner que les catégories finales (sans sous-catégories).
          <br />
          Sélectionner une catégorie finale inclura automatiquement toute sa hiérarchie parent.
          <br />
          🟢 = Catégorie sélectionnable • 🔒 = Catégorie avec sous-catégories (non sélectionnable)
        </div>

        {isDropdownOpen && (
          <div style={styles.dropdown}>
            {hierarchicalCategories.map(category => {
              const selectionState = getCategorySelectionState(category.id);
              const categoryHasChildren = hasChildren(category.id);
              const isSelectable = !categoryHasChildren;
              
              return (
                <div
                  key={category.id}
                  style={{
                    ...styles.categoryOption,
                    ...(categoryHasChildren ? styles.categoryOptionDisabled : {}),
                    backgroundColor: selectionState === 'selected' 
                      ? '#f0f9ff' 
                      : selectionState === 'partial' 
                        ? '#fef3c7' 
                        : 'transparent',
                    paddingLeft: `${12 + (category.level * 20)}px`
                  }}
                  onClick={() => isSelectable && handleCategoryToggle(category.id)}
                  title={categoryHasChildren ? 'Cette catégorie a des sous-catégories, veuillez sélectionner une catégorie finale' : 'Cliquer pour sélectionner'}
                >
                  <div style={styles.categoryText}>
                    <div style={styles.categoryName}>
                      {category.name}
                      {categoryHasChildren && (
                        <span style={styles.warningText}> 🔒</span>
                      )}
                      {!categoryHasChildren && (
                        <span style={styles.leafIndicator}> 🟢</span>
                      )}
                    </div>
                    <div style={styles.categoryPath}>
                      Niveau {category.level + 1}
                      {category.full_path && ` • ${category.full_path}`}
                      {categoryHasChildren && (
                        <span style={styles.warningText}>
                          <br />↳ Contient des sous-catégories - sélectionnez une catégorie finale
                        </span>
                      )}
                    </div>
                  </div>
                  {selectionState === 'selected' && !categoryHasChildren && (
                    <span style={{ color: '#059669', fontWeight: 'bold' }}>✓</span>
                  )}
                  {selectionState === 'partial' && (
                    <span style={{ color: '#d97706', fontWeight: 'bold' }}>◐</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategorySelectorMultiple;